import { z } from "zod";
import { formatPhone } from "./phone.js";
import { Labels } from "./vocabulary.js";
import { LayoutAxis } from "./estimateAxes.js";
import { MarkupRound } from "./referral.js";

// ---------------------------------------------------------------------------
// 自社（発行者）の情報（縦串4a 020 → 縦串5 024 → 縦串6 025、docs/20）と、郵便番号→住所。
// 会社名は平文、住所・郵便番号・建物名・電話・FAX・代表者名は暗号化（CLAUDE.md ルール5）。
// 振込先は 043（docs/32）で設定の別メニュー「振込口座」に分けた → bankAccount.ts。ここには既定の口座の1行（bankAccountText）だけ残す
// ---------------------------------------------------------------------------

export const COMPANY_MSG = {
  nameRequired: "会社名を入力してください。見積書の発行者として印字します",
  nameTooLong: "会社名は100文字までです",
  regNoInvalid: "登録番号は T＋13桁の数字です（例：T1234567890123）",
  zipInvalid: "郵便番号は7桁の数字です（例：560-0081）",
  imageType: "画像は PNG か JPEG を選んでください",
  imageTooBig: "画像は 2MB までです",
  imageTooLarge: "画像の大きさは 1500px までです（縮めてから選んでください）",
  imageBroken: "画像として読めませんでした。別のファイルを選んでください",
  imageUnreadable: "この画像は読み込めませんでした。PNG か JPEG の画像を選んでください（PDF・Illustrator・Word のファイルは使えません）",
  marginRange: "粗利率は 0〜99.9 の数字（小数1桁まで）で入れてください",
  discountLimitRange: "値引きとして処理できる上限は 0〜100,000円 の整数で入れてください",
  lumpsumPctRange: "一式の知らせの基準（％）は 0〜100 の整数で入れてください",
  lumpsumYenRange: "一式の知らせの基準（円）は 0 以上の整数で入れてください",
} as const;

export const ADDRESS_MSG = {
  zipInvalid: COMPANY_MSG.zipInvalid,
  notFound: "その郵便番号の住所が見つかりませんでした。郵便番号を確かめるか、住所を直接入れてください",
  pickOne: "この郵便番号には町域がいくつかあります。近いものを選んでください",
} as const;

const toHalf = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[－‐−ー]/g, "-");
/** 全角の数字を半角に（法人番号・登録番号・国税庁の所在地「２丁目１２番２号」） */
export const toHalfDigits = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

/** 郵便番号を7桁の数字に整える（「560-0081」「〒５６０−００８１」→「5600081」。7桁にならなければ null） */
export function normalizeZip(s: string): string | null {
  const d = toHalf(s).replace(/[^0-9]/g, "");
  return /^[0-9]{7}$/.test(d) ? d : null;
}
export const formatZip = (z: string) => (z.length === 7 ? `${z.slice(0, 3)}-${z.slice(3)}` : z);

/** 空か7桁。全角・ハイフン・〒 を受けて7桁に直す */
export const Zip = z
  .string()
  .trim()
  .transform((s, ctx) => {
    if (s === "") return "";
    const d = normalizeZip(s);
    if (!d) {
      ctx.addIssue({ code: "custom", message: COMPANY_MSG.zipInvalid });
      return z.NEVER;
    }
    return d;
  });

/** 電話・FAX：全角→半角、括弧と空白はハイフンに、ハイフン無しで桁がそろえば区切る（「0664294800」→「06-6429-4800」。phone.ts） */
export const PhoneLike = z
  .string()
  .trim()
  .max(20)
  .transform((s) => formatPhone(s));

/** 登録した画像（社印・ロゴ）。url はこの会社でログインしているときだけ開ける */
export const ImageRef = z.object({
  url: z.string(),
  contentType: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  bytes: z.number().int(),
});
export type ImageRef = z.infer<typeof ImageRef>;

/** 自社（発行者）の情報と設定。GET /api/v1/company */
export const Company = z.object({
  name: z.string(),
  /** 代表者名。入れると紙の会社名の下に印字（暗号化） */
  representativeName: z.string(),
  /** 会計年度の始まりの月（1〜12。既定 4） */
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  /** 郵便番号 7桁（ハイフンなし）。無ければ空（暗号化） */
  postalCode: z.string(),
  /** 住所（都道府県から番地まで1行。暗号化）。画面は「都道府県」と「市区町村・番地」に分けて見せる（address.ts） */
  address: z.string(),
  /** 住所の頭の都道府県のコード（1〜47、平文。030）。API が住所から決める。頭が都道府県でなければ null */
  prefCode: z.number().int().min(1).max(47).nullable(),
  /** 建物名（暗号化） */
  building: z.string(),
  phone: z.string(),
  fax: z.string(),
  /** 適格請求書発行事業者の登録番号（T＋13桁）。無ければ空 */
  invoiceRegistrationNo: z.string(),
  /** 既定の振込口座の1行（043、bankAccount.ts の bankAccountText）。口座が無ければ空。口座そのものは GET /bank-accounts */
  bankAccountText: z.string(),
  seal: ImageRef.nullable(),
  logo: ImageRef.nullable(),
  /** 出した見積・請求書を「取消し」して下書きに戻せるか（021。既定 ON） */
  allowQuoteUnissue: z.boolean(),
  /** 見積書 PDF（表紙＋内訳の紙）で内訳ごとに改ページするか（022。既定 OFF） */
  pdfPageBreakPerSheet: z.boolean(),
  /** 外注見積から行を取り込むときの粗利率（売価に対する％）の既定（049、docs/34 決めごと5。既定 20） */
  vendorMarginPct: z.number(),
  /** 売上の基準（#266、docs/96 決めごと9）：invoice＝請求日（既定）／delivery＝納品日／acceptance＝検収日（1b）。古い形の読み込みのため省けてよい */
  revenueBasis: z.enum(["invoice", "delivery", "acceptance"]).optional(),
  /** 入金の不足を「値引きとして処理」できる上限（円。E1、営業力 docs/08 決めごと4。既定 1,000）。画面は sales の会社だけ出す */
  shortfallDiscountLimit: z.number().int().optional(),
  /** 新しい見積の並べ方の既定（#129。既定 manual＝手で並べる） */
  defaultLayoutAxis: LayoutAxis,
  /** 印刷で前の行と同じ名称を「〃」にする（#129。既定 OFF。データは変えない） */
  printDitto: z.boolean(),
  /** 別途の一覧を見積書の備考の先頭に「別途：…」の1行で印字する（#308、docs/103 §3。既定 OFF＝新しい会社。#129 の「範囲の注記の一覧」の列を流用） */
  printScopeNotes: z.boolean(),
  /** 内訳のない一式を社内向けに知らせる基準（#130）：税抜合計のこの％を超えたら（0＝使わない。既定 10） */
  lumpsumWarnPct: z.number().int(),
  /** 同：この円を超えたら（0＝使わない。既定 100000） */
  lumpsumWarnYen: z.number().int(),
  /** 見積書の PDF の最後に「積算表」を付ける（#130。既定 OFF）。出すときに固める */
  printTakeoffSheet: z.boolean(),
  /** パートナー手数料を使うか（#213、docs/40 §2。モジュール referral。既定 OFF） */
  referralFee: z.boolean(),
  /** 上乗せ後の単価の丸め（#213。1／10／100円・切り上げ。既定 1） */
  markupRound: MarkupRound,
  /** この会社の言葉（027、docs/22）。紙の見出し（工事名／件名）や「顧客」に使う。変えるのは PUT /company/vocabulary */
  labels: Labels,
});
export type Company = z.infer<typeof Company>;

/** 登録番号（インボイス）：空か T＋13桁。小文字の t・全角も直す。自社（Company）と顧客（Partner）で共用 */
export const RegNo = z
  .string()
  .trim()
  .transform((s) => toHalf(s).replace(/^t/, "T"))
  .pipe(z.union([z.literal(""), z.string().regex(/^T\d{13}$/, COMPANY_MSG.regNoInvalid)]));

/** PUT /api/v1/company。**省いた欄は変えない**（発行の確認画面は会社名・住所・電話だけ送る）。会社名だけ必須 */
export const CompanyUpdate = z.object({
  name: z.string().trim().min(1, COMPANY_MSG.nameRequired).max(100, COMPANY_MSG.nameTooLong),
  representativeName: z.string().trim().max(60).optional(),
  fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
  postalCode: Zip.optional(),
  address: z.string().trim().max(200).optional(),
  building: z.string().trim().max(100).optional(),
  phone: PhoneLike.optional(),
  fax: PhoneLike.optional(),
  invoiceRegistrationNo: RegNo.optional(),
  allowQuoteUnissue: z.boolean().optional(),
  pdfPageBreakPerSheet: z.boolean().optional(),
  vendorMarginPct: z.number().min(0, COMPANY_MSG.marginRange).max(99.9, COMPANY_MSG.marginRange).multipleOf(0.1, COMPANY_MSG.marginRange).optional(),
  shortfallDiscountLimit: z.number().int(COMPANY_MSG.discountLimitRange).min(0, COMPANY_MSG.discountLimitRange).max(100_000, COMPANY_MSG.discountLimitRange).optional(),
  revenueBasis: z.enum(["invoice", "delivery", "acceptance"]).optional(),
  defaultLayoutAxis: LayoutAxis.optional(),
  printDitto: z.boolean().optional(),
  printScopeNotes: z.boolean().optional(),
  lumpsumWarnPct: z.number().int(COMPANY_MSG.lumpsumPctRange).min(0, COMPANY_MSG.lumpsumPctRange).max(100, COMPANY_MSG.lumpsumPctRange).optional(),
  lumpsumWarnYen: z.number().int(COMPANY_MSG.lumpsumYenRange).min(0, COMPANY_MSG.lumpsumYenRange).max(999_999_999_999, COMPANY_MSG.lumpsumYenRange).optional(),
  printTakeoffSheet: z.boolean().optional(),
  /** パートナー手数料（#213。管理者。モジュール referral を ON/OFF＝OFF でも登録・発行済みの見積は残る） */
  referralFee: z.boolean().optional(),
  /** 上乗せ後の単価の丸め（#213） */
  markupRound: MarkupRound.optional(),
});
export type CompanyUpdate = z.infer<typeof CompanyUpdate>;

// ---------------------------------------------------------------------------
// 郵便番号→住所（025 postal_codes）。GET /api/v1/address?zip=5600081
// ---------------------------------------------------------------------------

export const PostalCandidate = z.object({
  pref: z.string(),
  city: z.string(),
  /** 町域（掃除済み。市区町村までしか無い郵便番号は空） */
  town: z.string(),
  /** 元データの括弧の中（「１〜１９丁目」「上長者町通堀川東入…」）。候補を選ぶときの添え書き */
  townNote: z.string().nullable(),
  prefKana: z.string(),
  cityKana: z.string(),
  townKana: z.string(),
});
export type PostalCandidate = z.infer<typeof PostalCandidate>;

export const AddressLookup = z.object({
  zip: z.string(),
  candidates: z.array(PostalCandidate),
});
export type AddressLookup = z.infer<typeof AddressLookup>;

/** 候補を住所の1行に（都道府県＋市区町村＋町域）。番地は使う人が続けて打つ */
export const candidateText = (c: PostalCandidate) => `${c.pref}${c.city}${c.town}`;
